import { useCallback } from 'react';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const ANALYTICS_URL = `${SUPABASE_URL}/functions/v1/analytics`;

interface AnalyticsResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export const useAnalytics = () => {
  const sendAnalyticsEvent = useCallback(async (type: string, data: Record<string, string>): Promise<AnalyticsResponse> => {
    try {
      const response = await fetch(ANALYTICS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ type, data }),
      });

      const text = await response.text();
      if (!text) {
        return { success: false, error: 'Empty response from server' };
      }

      let jsonData: AnalyticsResponse;
      try {
        jsonData = JSON.parse(text);
      } catch {
        if (import.meta.env.DEV) {
          console.error('Failed to parse analytics response:', text);
        }
        return { success: false, error: 'Invalid JSON response' };
      }

      if (!response.ok) {
        return { success: false, error: jsonData.error || `HTTP error: ${response.status}` };
      }

      return jsonData;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error(`Analytics error (${type}):`, error);
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }, []);

  const trackPageView = useCallback(async (page: string) => {
    if (!page) return { success: false, error: 'Page parameter is required' };
    return sendAnalyticsEvent('pageview', { page });
  }, [sendAnalyticsEvent]);

  const trackProductInteraction = useCallback(async (productId: string, interactionType: string) => {
    if (!productId || !interactionType) {
      return { success: false, error: 'Product ID and interaction type are required' };
    }
    return sendAnalyticsEvent('product', { productId, interactionType });
  }, [sendAnalyticsEvent]);

  const trackContactSubmission = useCallback(async (email: string) => {
    if (!email) return { success: false, error: 'Email is required' };
    return sendAnalyticsEvent('contact', { email });
  }, [sendAnalyticsEvent]);

  return {
    trackPageView,
    trackProductInteraction,
    trackContactSubmission
  };
};
